import { assign, setup } from "xstate";
import {
  FilterWizardEventType,
  FilterWizardStates
} from "./types.js";
import { buildEventData, calculateTotalSteps, isValidAnswer } from "./utils.js";
const createFilterWizardMachine = ({
  questions,
  onEvent
}) => {
  const totalSteps = calculateTotalSteps(questions.question);
  return setup({
    actions: {
      completeWizard: ({ context, event }) => {
        if (event.type !== FilterWizardEventType.Next)
          return;
        context.onEvent?.(
          FilterWizardEventType.Complete,
          buildEventData(
            context.currentQuestion,
            context.lastSelectedAnswer,
            context.questionPath.length + 1
          )
        );
      },
      enterWizard: ({ context, event }) => {
        if (event.type === FilterWizardEventType.Init) {
          context.onEvent?.(
            FilterWizardEventType.Enter,
            buildEventData(context.currentQuestion, null, 1)
          );
        }
      },
      exitWizard: ({ context, event }) => {
        if (event.type !== FilterWizardEventType.Exit)
          return;
        context.onEvent?.(
          FilterWizardEventType.Exit,
          buildEventData(
            context.currentQuestion,
            context.lastSelectedAnswer,
            context.questionPath.length + 1
          )
        );
      },
      goBack: assign(({ context, event }) => {
        if (event.type !== FilterWizardEventType.Back)
          return context;
        const previousQuestion = context.questionPath[context.questionPath.length - 1];
        const targetQuestion = previousQuestion ?? context.questions.question;
        context.onEvent?.(
          FilterWizardEventType.Back,
          buildEventData(
            targetQuestion,
            context.lastSelectedAnswer,
            Math.max(1, context.questionPath.length) + 1
          )
        );
        return {
          currentQuestion: targetQuestion,
          lastSelectedAnswer: null,
          questionPath: context.questionPath.slice(0, -1)
        };
      }),
      resetWizard: assign(({ context, event }) => {
        if (event.type !== FilterWizardEventType.Reset)
          return context;
        return {
          currentQuestion: questions.question,
          lastSelectedAnswer: null,
          onEvent,
          questionPath: [],
          questions,
          totalSteps
        };
      }),
      selectAnswer: assign(
        ({ context, event }) => {
          if (event.type !== FilterWizardEventType.Next)
            return context;
          const answer = event.answer;
          if (answer.question) {
            context.onEvent?.(
              FilterWizardEventType.Next,
              buildEventData(
                context.currentQuestion,
                answer,
                context.questionPath.length + 1
              )
            );
          }
          return {
            currentQuestion: !isValidAnswer(answer) || !answer.question ? context.currentQuestion : answer.question,
            // If answer has a nested question, add current question to path
            questionPath: answer.question ? [...context.questionPath, context.currentQuestion] : context.questionPath,
            lastSelectedAnswer: isValidAnswer(event.answer) ? event.answer : null
          };
        }
      )
    },
    guards: {
      canGoBack: ({ context }) => context.questionPath.length > 0,
      isCompleted: ({ event }) => {
        if (event.type !== FilterWizardEventType.Next)
          return false;
        return !event.answer.question;
      }
    },
    types: {
      context: {},
      events: {}
    }
  }).createMachine({
    context: {
      currentQuestion: questions.question,
      lastSelectedAnswer: null,
      onEvent,
      questionPath: [],
      questions,
      totalSteps
    },
    id: "filter-wizard",
    initial: FilterWizardStates.Active,
    states: {
      [FilterWizardStates.Active]: {
        entry: ["enterWizard"],
        on: {
          [FilterWizardEventType.Next]: [
            {
              actions: ["selectAnswer"],
              guard: "isCompleted",
              target: FilterWizardStates.Completed
            },
            {
              actions: ["selectAnswer"],
              target: FilterWizardStates.Active
            }
          ],
          [FilterWizardEventType.Back]: {
            actions: ["goBack"],
            guard: "canGoBack",
            target: FilterWizardStates.Active
          },
          [FilterWizardEventType.Exit]: {
            target: FilterWizardStates.Exited
          },
          [FilterWizardEventType.Reset]: {
            actions: ["resetWizard"],
            target: FilterWizardStates.Active
          }
        }
      },
      [FilterWizardStates.Exited]: {
        entry: ["exitWizard"],
        type: "final"
      },
      [FilterWizardStates.Completed]: {
        entry: ["completeWizard"],
        type: "final"
      }
    }
  });
};
export {
  createFilterWizardMachine
};
